import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@core-ui-lib/utils';

import { Mic, Square } from 'lucide-react';

import { useTwilioVoice } from '@hooks/useTwilioVoice';
import useVoicemailApi from '@features/voicemail/hooks/useVoicemailApi';
import { useSessionStore } from '@features/auth';
import { logSuccess } from '@lib/logger/logSuccess';
import { logAndToast } from '@lib/logger/logAndToast';

import type { VoicemailDto } from '../../types/wizard.types';

interface VoicemailRecordPanelProps {
  onCreated: (voicemail: VoicemailDto) => void;
}

function FakeWave ({ animated = false }: { animated?: boolean }): React.JSX.Element {
  const bars = 20;
  const heights = useMemo(
    () => Array.from({ length: bars }).map((_, i) => 15 + ((i * 17) % 60)),
    [],
  );

  return (
    <div className="flex items-center justify-center gap-[3px] h-12">
      {heights.map((h, i) => (
        <span
          key={i}
          className={cn(
            'w-[3px] rounded-sm transition-all',
            animated ? 'bg-emerald-500 animate-pulse' : 'bg-muted-foreground/20',
          )}
          style={{
            height: animated ? h : h * 0.4,
            animationDelay: animated ? `${i * 80}ms` : undefined,
          }}
        />
      ))}
    </div>
  );
}

export default function VoicemailRecordPanel ({
  onCreated,
}: VoicemailRecordPanelProps): React.JSX.Element {
  const { t } = useTranslation();
  const { currentUser } = useSessionStore();
  const userId = String(currentUser?.userId ?? '');
  const { startCall, sendDigits, currentCall, status: twilioStatus } = useTwilioVoice(userId);
  const { getVoicemailsBySdrIdAsync } = useVoicemailApi();

  const [title, setTitle] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const latestTitleRef = useRef('');

  useEffect(() => {
    latestTitleRef.current = title;
  }, [title]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const canUseTwilio = twilioStatus === 'ready' || twilioStatus === 'busy';
  const canStart = title.trim() !== '' && canUseTwilio && !isRecording;

  const formatTimer = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleStartRecording = useCallback(async (): Promise<void> => {
    const clean = title.trim();
    if (clean === '' || !canUseTwilio) {
      return;
    }

    // Request mic permission
    let stream: MediaStream | null = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      await logAndToast({
        message: t('voice.voicemails.toast.micDenied', 'Microphone access denied. Please allow microphone access to record.'),
        source: 'VoicemailRecordPanel',
        toast: true,
        level: 'error',
      });
      return;
    } finally {
      stream?.getTracks().forEach((track) => {
        track.stop();
      });
    }

    await startCall(userId, {
      isVoicedrop: 'true',
      agent: userId,
      recordTitle: clean,
      newRecording: 'true',
      recordId: '',
    });

    const call = currentCall();
    if (call != null) {
      call.on('disconnect', () => {
        setIsRecording(false);
        if (timerRef.current !== null) {
          clearInterval(timerRef.current);
        }
        // Fetch the new voicemail after a short delay
        setTimeout(() => {
          void (async () => {
            const result = await getVoicemailsBySdrIdAsync(userId);
            if (result.success && result.data !== undefined && result.data.length > 0) {
              const savedTitle = latestTitleRef.current.trim();
              const match = result.data.find((vm) => vm.title === savedTitle) ?? result.data[0];
              await logSuccess(
                t('voice.voicemails.toast.created', 'Voicemail created successfully'),
                'VoicemailRecordPanel',
                { voicemailId: match.id ?? match._id, title: match.title },
              );
              onCreated(match);
            } else {
              await logAndToast({
                message: t('voice.voicemails.toast.createFailed', 'Failed to save voicemail recording'),
                source: 'VoicemailRecordPanel',
                toast: true,
                level: 'error',
              });
            }
          })();
        }, 700);
      });
    }

    setIsRecording(true);
    setRecordingSeconds(0);
    timerRef.current = setInterval(() => {
      setRecordingSeconds((prev) => prev + 1);
    }, 1000);
  }, [title, canUseTwilio, startCall, currentCall, userId, getVoicemailsBySdrIdAsync, onCreated]);

  const handleStopRecording = useCallback((): void => {
    if (!isRecording) {
      return;
    }
    sendDigits('*');
    setIsRecording(false);
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
    }
  }, [isRecording, sendDigits]);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="text-sm font-medium text-muted-foreground">
          {t('wizard.inlineCreate.voicemail.nameLabel', 'Voicemail Name')} <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value); 
          }}
          placeholder={t('voice.voicemails.create.namePh', 'Voicemail name')}
          disabled={isRecording}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        />
      </div>

      <div className="rounded-xl border border-border bg-muted/30 px-4 py-6">
        {isRecording ? (
          <div className="flex flex-col items-center gap-4">
            <FakeWave animated />

            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse" />
              <span className="font-mono font-medium">{formatTimer(recordingSeconds)}</span>
              <span>{t('wizard.inlineCreate.voicemail.recording', 'recording...')}</span>
            </div>

            <button
              type="button"
              onClick={handleStopRecording}
              className="flex items-center gap-2 rounded-lg bg-gray-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-gray-600 transition-colors"
            >
              <Square className="h-4 w-4" />
              {t('wizard.inlineCreate.voicemail.stopRecording', 'Stop Recording')}
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <Mic className="h-8 w-8 text-red-500" />
            </div>

            <p className="text-sm text-muted-foreground text-center">
              {t('wizard.inlineCreate.voicemail.tapToRecord', 'Click the button below to start recording your voicemail')}
            </p>

            <button
              type="button"
              onClick={() => {
                void handleStartRecording(); 
              }}
              disabled={!canStart}
              className={cn(
                'flex items-center gap-2 rounded-lg bg-red-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-red-600 transition-colors',
                !canStart && 'opacity-50 cursor-not-allowed',
              )}
            >
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-white" />
              {t('wizard.inlineCreate.voicemail.startRecording', 'Start Recording')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
